import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { publicLayoutRoute } from '@/ui/pages/public'
import { resolveLanguage } from '@/application/i18n/language'
import { useDocumentMeta } from '@/application/hooks/useDocumentMeta'
import { OG_IMAGE_URL } from '@/application/seo/site'
import { useLocalizedUrl } from '@/application/hooks/useLocalizedUrl'
import { VOICE_CATALOGUE, PIPER_VOICES_REVISION } from '@/application/tts/voice-catalogue'
import { LegalPageLayout } from '@/ui/pages/public/legal/LegalPageLayout'
import { formatLastUpdated } from '@/ui/pages/public/legal/lastUpdated'

export const audioCreditsRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'credits-audio',
  component: AudioCreditsPage,
})

const ESPEAK_SOURCES_URL = 'https://github.com/espeak-ng/espeak-ng'
const PIPER_URL = 'https://github.com/rhasspy/piper'
const PIPER_VOICES_URL = 'https://huggingface.co/rhasspy/piper-voices'
const ONNX_RUNTIME_URL = 'https://github.com/microsoft/onnxruntime'

/**
 * Attributions for the "Listen to this article" site voices and the
 * open-source software that runs them (voice licences, Piper, the GPL-3
 * eSpeak NG phonemizer with a link to its sources, ONNX Runtime Web).
 */
export function AudioCreditsPage() {
  const { t, i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)
  const pageUrl = useLocalizedUrl('/credits-audio')

  useDocumentMeta({
    title: t('seo.audio_credits_title'),
    description: t('seo.audio_credits_description'),
    imageUrl: OG_IMAGE_URL,
    url: pageUrl,
    type: 'website',
  })

  return (
    <LegalPageLayout
      title={t('audio_credits.page_title')}
      lastUpdatedLabel={formatLastUpdated(lang)}
    >
      <p>{t('audio_credits.intro')}</p>

      <section>
        <h2>{t('audio_credits.voices_title')}</h2>
        <ul>
          {Object.values(VOICE_CATALOGUE).map((voice) => (
            <li key={voice.id}>
              <strong>{voice.id}</strong> — {t('audio_credits.voice_license')} : {voice.license}.{' '}
              {t('audio_credits.voice_credit')} : {voice.attribution}.
            </li>
          ))}
        </ul>
        <p>
          {t('audio_credits.voice_source', { revision: PIPER_VOICES_REVISION })}{' '}
          <a href={PIPER_VOICES_URL} target="_blank" rel="noopener noreferrer">
            {PIPER_VOICES_URL}
          </a>
        </p>
      </section>

      <section>
        <h2>{t('audio_credits.piper_title')}</h2>
        <p>
          {t('audio_credits.piper_body')}{' '}
          <a href={PIPER_URL} target="_blank" rel="noopener noreferrer">
            {PIPER_URL}
          </a>
        </p>
      </section>

      <section>
        <h2>{t('audio_credits.espeak_title')}</h2>
        <p>
          {t('audio_credits.espeak_body')}{' '}
          <a href={ESPEAK_SOURCES_URL} target="_blank" rel="noopener noreferrer">
            {ESPEAK_SOURCES_URL}
          </a>
        </p>
      </section>

      <section>
        <h2>{t('audio_credits.onnx_title')}</h2>
        <p>
          {t('audio_credits.onnx_body')}{' '}
          <a href={ONNX_RUNTIME_URL} target="_blank" rel="noopener noreferrer">
            {ONNX_RUNTIME_URL}
          </a>
        </p>
      </section>
    </LegalPageLayout>
  )
}
